import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useAppDispatch } from "../../../app/hooks";
import { OrganizationRole } from "../../instructors/enums/OrganizationRole";
import { createInvitation } from "../slice-extensions/organization-slice-extension";
import FormModal from "../../../components/billing/FormModal";

interface InviteInstructorModalProps {
  organizationId: string;
  onClose: () => void;
}

/**
 * Modal for inviting an instructor by email with an Admin/Instructor role.
 * A 409 (duplicate invitation or existing member) shows the server message inline.
 */
const InviteInstructorModal: React.FC<InviteInstructorModalProps> = ({
  organizationId,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrganizationRole>(OrganizationRole.Instructor);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (email.trim() === "") {
      setError(t("organization.inviteModal.emailRequired", "Please enter an email address."));
      return;
    }
    setSubmitting(true);
    const result = await dispatch(
      createInvitation({ organizationId, email: email.trim(), role })
    );
    setSubmitting(false);
    if (createInvitation.fulfilled.match(result)) {
      onClose();
    } else {
      setError(result.payload?.message ?? t("organization.inviteModal.sendFailed", "Failed to send the invitation"));
    }
  };

  return (
    <FormModal onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-4 lg:p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3>{t("organization.inviteModal.title", "Invite instructor")}</h3>
            <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
              {t("organization.inviteModal.subtitle", "They'll receive an email with a link to join your organization.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("organization.inviteModal.close", "Close")}
            className="text-grey-50 hover:text-grey-30 dark:text-grey-60 dark:hover:text-grey-40"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="invite_email" className="label">
              {t("organization.inviteModal.emailLabel", "Email")}
            </label>
            <input
              id="invite_email"
              type="email"
              className="input w-full"
              placeholder={t("organization.inviteModal.emailPlaceholder", "colleague@example.com")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              required
            />
          </div>
          <div>
            <label htmlFor="invite_role" className="label">
              {t("organization.inviteModal.roleLabel", "Role")}
            </label>
            <select
              id="invite_role"
              className="input w-full"
              value={role}
              onChange={(e) => setRole(e.target.value as OrganizationRole)}
              disabled={submitting}
            >
              <option value={OrganizationRole.Instructor}>
                {t("organization.roles.Instructor", "Instructor")}
              </option>
              <option value={OrganizationRole.Admin}>
                {t("organization.roles.Admin", "Admin")}
              </option>
            </select>
            <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
              {t("organization.inviteModal.roleHint", "Admins can manage billing and the organization; Instructors see only their own schedule.")}
            </p>
          </div>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {t("organization.inviteModal.send", "Send invitation")}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default InviteInstructorModal;
